import { useEffect, useState } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { SelectionButton } from '~/bootcamp/Activities/Fractions/components/SelectionButton'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { getRangesByDifficulty } from './constants'
import { generateTerms, QuestionValue } from './helpers'

function FractionCancelFactors({
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC,
}: ActivityProps): React.ReactElement {
  const [answer, setAnswer] = useState<number | null>(null)
  const [multipleQuestions, setMultipleQuestions] = useState<QuestionValue[]>([])
  const [numerator, setNumerator] = useState<number>(1)
  const [denominator, setDenominator] = useState<number>(2)
  const [taskHistory, setTaskHistory] = useState<number[]>([])

  const { maxVariation } = getRangesByDifficulty(difficultyLevel)

  // Option values in painted order; `isAns`/`isSelected` stay out. No rendered input, so no \square slot.
  useEmitInstance(
    onInstanceChange,
    denominator && multipleQuestions.length > 0
      ? `\\frac{${numerator}}{${denominator}}; options: ${multipleQuestions.map((item) => item.value).join(', ')}`
      : null,
  )

  useEffect(() => {
    setAnswer(null)

    const { numerator: num, denominator: denom, multipleQues } = generateTerms(difficultyLevel, taskHistory)

    setNumerator(num)
    setDenominator(denom)
    setMultipleQuestions(multipleQues)
  }, [difficultyLevel, taskHistory])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const ansResult = answer !== null ? multipleQuestions[answer].isAns : false
    onAnswerStateChange(ansResult)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      if (answer !== null) return [multipleQuestions[answer].value, ...prevState].slice(0, maxVariation)
      else return [...prevState].slice(0, maxVariation)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
  }

  const handleOnOptionClick = (index: number): void => {
    multipleQuestions.map((item) => (item.isSelected = false))
    multipleQuestions[index].isSelected = true
    setAnswer(index)

    setMultipleQuestions([...multipleQuestions])
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="w-6 flex flex-col items-center">
          {numerator}
          <Divider />
          {denominator}
        </div>
        <div className=" w-full flex space-x-3 md:space-x-8 pt-10">
          {multipleQuestions.map((item, index) => (
            <SelectionButton
              isSelected={item.isSelected}
              isWrong={item.isSelected === true && answerState === false}
              key={item.id}
              onClick={() => {
                handleOnOptionClick(index)
              }}
              size="medium"
            >
              {item.value}
            </SelectionButton>
          ))}
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== null,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => {
            multipleQuestions.map((item) => (item.isSelected = false))
            setAnswer(null)
          },
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default FractionCancelFactors
